import { FC, useMemo, useRef, useState } from 'react';
import { HarmReportProps, HarmReportsKebabOption } from './harmReports.type';
import { HarmReportsTableHeader } from './harmReportsTableHeader';
import { HarmReportsTableFilter } from './harmReportsTableFilter';
import Table from '../../component/table/table';
import {
  formatSelectedReportRefs,
  getHarmReportsKebabOptions,
  getHarmReportsTableHeader,
  getHarmReportTitle,
} from './harmReportesHelper';
import HarmReportForm from '../../component/harmReportForm/harmReportForm';
import { useHarmReports } from '../../hooks/useHarmReports';
import { HarmReportDetails } from './harmReportDetails';
import { getConsecutiveSweeps } from '../../utils/helper';
import { Trans } from 'react-i18next';
import UserVerificationModal from '../../component/userVerificationModal/userVerificationModal';
import { ConfirmationModalV2 } from '../../component/confirmationModalV2/confirmationModalV2';
import SelectPlayerModal from '../../component/selectPlayerModal/selectPlayerModal';
import { InterventionReportDetails } from '../interventionReports/interventionReportDetails';
import { InterventionReport } from '@inplay/models';
import { ReportService } from '../../service/reportService';
import ModalComponent from '../../component/modal/modal';
import Loading from '../../assets/loading/loading';

export const HarmReports: FC<HarmReportProps> = () => {
  const {
    handleApplyFilters,
    handleClearFilters,
    handleOnChangeSearch,
    handleSelectionChange,
    filters,
    handleReportHarmClose,
    handleKebabActionFromKebabMenu,
    harmReports,
    isTablet,
    loading,
    openCreateHarm,
    isReviewAndEditMode,
    openFilterModal,
    toggleFilterModal,
    selectedVenueBranch,
    handleDateApply,
    selectedRows,
    setSelectedRows,
    selectedHarmReport,
    handleRowClick,
    closeHarmReportDetails,
    handleReviewAndSignClick,
    organizationContextLoading,
    harmReportForCreate,
    fetchHarmReports,
    t,
    handleDeleteHarmReports,
    handleDeleteClick,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deleting,
    handleOnVerify,
    setShowUserVerification,
    showUserVerification,
    handleReportHarmClick,
    setShowSelectPlayerModal,
    showSelectPlayerModal,
    handleOnProceed,
    handleSettingMenuSelect,
    verifiedUser,
  } = useHarmReports();

  const tooltipParentRef = useRef(null);
  const reportService = useMemo(() => new ReportService(), []);
  const [viewingIntervention, setViewingIntervention] = useState<InterventionReport | null>(null);
  const [loadingIntervention, setLoadingIntervention] = useState<boolean>(false);

  const handleViewLinkedIntervention = async (interventionId: string) => {
    setLoadingIntervention(true);
    if (!selectedVenueBranch) return;
    try {
      const response = await reportService.getInterventionReports({
        venueBranchId: selectedVenueBranch._id,
        currentDate: new Date(),
      });
      const reports: InterventionReport[] = response.data?.reports ?? [];
      const found = reports.find((r) => r.originalReportId === interventionId);
      if (found) setViewingIntervention(found);
    } catch (error) {
      console.error('[HarmReports] Error fetching intervention report', error);
    } finally {
      setLoadingIntervention(false);
    }
  };

  return (
    <div className="header-content">
      <div
        className="table-content pt-6 flex flex-col overflow-hidden"
        style={{ boxShadow: '0px 0px 25px #2F41A51F' }}
      >
        {showUserVerification && (
          <UserVerificationModal
            show={showUserVerification}
            onToggle={() =>
              setShowUserVerification((currentValue: boolean) => !currentValue)
            }
            onVerified={handleOnVerify}
            scope={'hrmc'}
          />
        )}
        {showSelectPlayerModal && (
          <SelectPlayerModal
            onClose={() => {
              setShowSelectPlayerModal(false);
            }}
            onProceed={handleOnProceed}
          />
        )}
        <HarmReportsTableHeader
          searchQuery={filters.searchQuery}
          handleOnChangeSearch={handleOnChangeSearch}
          selectedCount={selectedRows.length}
          reportLabel={
            selectedRows.length > 1
              ? t('common.multi_report')
              : t('sweeps.report_one')
          }
          selectedDate={filters.selectedDate}
          disableSettingMenu={false}
          onSettingMenuSelect={handleSettingMenuSelect}
          handleReportHarmClick={handleReportHarmClick}
          handleSelectedDate={handleDateApply}
          handleClose={() => setSelectedRows([])}
          handleDelete={handleDeleteClick}
          handleDownload={() => {
            // TODO: Implement download functionality
            console.log('Download selected reports:', selectedRows);
          }}
          showDownloadLoading={false}
        >
          <HarmReportsTableFilter
            isOpenUserModal={openFilterModal}
            selectedBranchIDs={
              selectedVenueBranch ? [selectedVenueBranch._id] : []
            }
            handleClearFilters={handleClearFilters}
            handleApplyFilters={handleApplyFilters}
            toggleFilterModal={toggleFilterModal}
          />
        </HarmReportsTableHeader>
        {/* Table    */}
        <div className="flex-grow overflow-y-auto max-h-[700px]">
          <Table
            onSelectionChange={handleSelectionChange}
            tableHeader={getHarmReportsTableHeader(
              isTablet,
              t,
              tooltipParentRef,
              handleViewLinkedIntervention,
            )}
            selectedRows={selectedRows}
            tableData={harmReports}
            loading={loading || organizationContextLoading}
            onViewDetail={handleRowClick}
            onKebabSelect={handleKebabActionFromKebabMenu}
            loadingMenu={{ rowIndex: -1, action: '' }}
            getKebabMenuOptions={getHarmReportsKebabOptions as any}
          />
        </div>
        {openCreateHarm && (
          <HarmReportForm
            title={getHarmReportTitle(
              harmReportForCreate?.reportRefNo,
              isReviewAndEditMode,
              t,
            )}
            data={harmReportForCreate}
            player={null}
            consecutiveSweeps={getConsecutiveSweeps(harmReportForCreate)}
            harmCount={null}
            interventionOffered={null}
            interventionRequested={null}
            isReviewAndEditMode={isReviewAndEditMode}
            onBack={handleReportHarmClose}
            verifiedUser={verifiedUser}
            onSuccess={fetchHarmReports}
          />
        )}
        {selectedHarmReport && (
          <HarmReportDetails
            handleReviewAndSignClick={handleReviewAndSignClick}
            harmReport={selectedHarmReport}
            closeHarmReportDetails={closeHarmReportDetails}
            onKebabAction={(action: HarmReportsKebabOption) => {
              closeHarmReportDetails();
              handleKebabActionFromKebabMenu(action, selectedHarmReport, 0);
            }}
          />
        )}
        {loadingIntervention && !viewingIntervention && (
          <ModalComponent
            onToggle={() => setLoadingIntervention(false)}
            open={true}
            title=""
            isShowClose
            footerHidden
            dialogClassName="w-full max-w-[900px] h-[90vh] overflow-hidden"
          >
            <div className="flex justify-center items-center h-[calc(90vh-100px)]">
              <Loading />
            </div>
          </ModalComponent>
        )}
        {viewingIntervention && (
          <InterventionReportDetails
            interventionReport={viewingIntervention}
            onClose={() => setViewingIntervention(null)}
            onReviewAndSign={() => setViewingIntervention(null)}
          />
        )}
        {showDeleteConfirmation && (
          <ConfirmationModalV2
            note="harmReports.deletionNote"
            title="common.deleteReport"
            isOpen={showDeleteConfirmation}
            showConfirmButton
            onClose={() => setShowDeleteConfirmation(false)}
            message={
              <Trans
                i18nKey="harmReports.deleteConfirmation"
                values={{
                  value: formatSelectedReportRefs(selectedRows),
                  report:
                    selectedRows.length === 1
                      ? t('harmReports.singleReport')
                      : t('harmReports.multipleReports'),
                }}
                components={{ bold: <strong /> }}
              />
            }
            isLoading={deleting}
            onConfirm={handleDeleteHarmReports}
          />
        )}
      </div>
    </div>
  );
};
